import React, { useState, useEffect, useRef } from 'react';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  addDays, 
  isSameMonth, 
  isSameDay, 
  isToday,
  isBefore,
  startOfDay
} from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

interface DatePickerProps {
  selectedDate: string | null;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: Date;
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ 
  selectedDate, 
  onChange, 
  placeholder = "Select date", 
  minDate = new Date(),
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };

    const handleResize = () => {
      if (isOpen) setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // Capture phase to handle nested scrolls
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(new Date(selectedDate));
    }
  }, [selectedDate]);

  const toggleOpen = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      // Check if there is enough space below, otherwise open upwards (optional, keeping simple for now)
      setPosition({
        top: rect.bottom + 8, // 8px gap
        left: rect.left
      });
    }
    setIsOpen(!isOpen);
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const onDateClick = (day: Date) => {
    onChange(format(day, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-4 px-2">
        <button
          onClick={(e) => { e.stopPropagation(); prevMonth(); }}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
        >
          <ChevronLeftIcon className="h-5 w-5" />
        </button>
        <div className="text-sm font-semibold text-gray-900">
          {format(currentMonth, 'MMMM yyyy')}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); nextMonth(); }}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-600"
        >
          <ChevronRightIcon className="h-5 w-5" />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const dateFormat = "EEEEE";
    const startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="text-xs font-medium text-gray-400 text-center py-1">
          {format(addDays(startDate, i), dateFormat)}
        </div>
      );
    }

    return <div className="grid grid-cols-7 mb-2">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const cloneDay = day;
        const isDisabled = isBefore(day, startOfDay(minDate));
        const isSelected = selectedDate ? isSameDay(day, new Date(selectedDate)) : false;
        const isCurrentMonth = isSameMonth(day, monthStart);

        days.push(
          <div
            key={day.toString()}
            className={`
              relative p-1 w-full aspect-square flex items-center justify-center text-sm rounded-full cursor-pointer transition-all duration-200
              ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-700'}
              ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'hover:bg-teal-50 hover:text-teal-600'}
              ${isSelected ? 'bg-teal-600 text-white hover:bg-teal-700 hover:text-white shadow-md' : ''}
              ${isToday(day) && !isSelected ? 'border border-teal-500 text-teal-600 font-medium' : ''}
            `}
            onClick={(e) => {
              e.stopPropagation();
              if (!isDisabled) onDateClick(cloneDay);
            }}
          >
            {formattedDate}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7 gap-1" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="space-y-1">{rows}</div>;
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div
        onClick={toggleOpen}
        className={`
          w-full px-4 py-3 border rounded-lg flex items-center cursor-pointer transition-all duration-200
          ${isOpen ? 'border-teal-500 ring-2 ring-teal-500/20' : 'border-gray-300 hover:border-gray-400'}
          bg-white
        `}
      >
        <CalendarDaysIcon className={`h-5 w-5 mr-3 ${selectedDate ? 'text-teal-600' : 'text-gray-400'}`} />
        <span className={`flex-1 ${selectedDate ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedDate ? format(new Date(selectedDate), 'MMM dd, yyyy') : placeholder}
        </span>
      </div>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: position.top,
                left: position.left,
                zIndex: 10001 // Higher than modal
              }}
              className="w-72 bg-white rounded-xl shadow-xl border border-gray-100 p-4"
            >
              {renderHeader()}
              {renderDays()}
              {renderCells()}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
